import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { handleCreateFamilyMember } from "../../service/FamilyMemberApi"; // Import the function
import '../general/General.css';
import { businessNames, relationShips } from '../general/DropDownValuesConfig';
import { fetchFamilyById } from "../../service/FamilyApi";
import { useCheckAccess } from "../../utils/Utilities";
import { translateText } from "../../service/UtilApi"; // Import translation function
import { showErrorToast, } from "../../utils/Toasts";
import "react-datepicker/dist/react-datepicker.css";
import { useForm } from "react-hook-form";
import { formatDateToISO, getYearOptions, maskAadhar } from "../../utils/Utilities";

const FamilyMemberCreate = () => {
  useCheckAccess('FamilyRW'); // Check if the user is logged in (if no user data, redirect to login)
  const { familyId } = useParams();
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState(null);

  const [loading, setLoading] = React.useState(false);
  const dateInputRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState("");

  const { watch, trigger, clearErrors, register, handleSubmit, formState: { errors, isValid, isSubmitting }, setValue } = useForm({ mode: "all" });
  const [nameData, setNameData] = useState({
    tempFirstName: "",
    tempLastName: "",
    firstName: "",
    lastName: "",
  });
  const relationship = watch("relationship");
  const aadhar = watch("aadhar");

  // Extract filtered family members with 12-digit Aadhaar
  const validFamilyMembers = familyData?.family?.familyMembers?.filter((member) =>
    /^\d{12}$/.test(member.maskedAadhar)
  ) || [];

  // Auto-set parentId if relationship is NA and no family members
  useEffect(() => {
    if (relationship === "இல்லை" && validFamilyMembers.length === 0 && aadhar) {
      setValue("parentId", aadhar);
    }
  }, [relationship, aadhar, setValue]);

  // Fetch family data
  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        if (familyId) {
          const data = await fetchFamilyById(familyId); // Await the fetchFamilyById function
          setFamilyData(data); // Set the family data in state
        }
      } catch (error) {
        showErrorToast("Error fetching family data");
      }
    };

    fetchFamilyData(); // Call the async function inside useEffect
  }, [familyId]); // Only re-run if id changes

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);

    const dateString = `${year}-06-01`;

    // Update both DOM and react-hook-form state
    if (dateInputRef.current) {
      dateInputRef.current.value = dateString;
    }
    setValue("dob", dateString, { shouldValidate: true });
  };



  const handleNameChange = (field, value) => {
    setNameData((prevData) => ({
      ...prevData,
      [field]: value, // Dynamically set the correct field
    }));
  };
  // Translate the first name to Tamil
  const translateNameToTamil = async () => {

    try {
      if (nameData.tempFirstName) {

        const translatedFirstName = await translateText(nameData.tempFirstName);
        setNameData((prevData) => ({
          ...prevData,
          firstName: translatedFirstName + " ",
        }));
      }
      if (nameData.tempLastName) {
        const translatedLastName = await translateText(nameData.tempLastName);
        setNameData((prevData) => ({
          ...prevData,
          lastName: translatedLastName + " ",
        }));
      }
    } catch (error) {
      console.error("Error translating name:", error);
    }
  };
  const handleFormSubmit = async (data) => {
    if (loading) {
      return;  // Prevent form submission if loading is true
    }
    if (!nameData.firstName || nameData.firstName.length < 3) {
      alert("பெயர் சரியாக உள்ளிடப்படவில்லை.");
      return;
    }

    // Clean the Aadhaar field specifically (remove spaces)
    if (data.aadhar) {
      data.aadhar = data.aadhar.replace(/\s+/g, ''); // Remove all spaces from Aadhaar
    }
    if (data.aadhar && (data.aadhar.startsWith("TMP") || data.aadhar.startsWith("00"))) {
      data.status = "draft";
    }
    else {
      data.status = "active";
    }

    setLoading(true); // Set loading state to true before starting API call

    const payload = {
      ...data,
      dob: formatDateToISO(data.dob),
      firstName: nameData.firstName.trim(),
      lastName: nameData.lastName.trim(),
    };
    try {
      const success = await handleCreateFamilyMember(payload);
      if (success) {
        navigate(`/family/view/${familyId}`); // Navigate after success
      }
    } catch (error) {
      console.error("Error creating family member:", error);
    } finally {
      setLoading(false); // Reset loading state after API call
    }
  };
  const handleChange = async (fieldName) => {
    await trigger(fieldName);  // Trigger field validation
    clearErrors(fieldName);  // Clear error if valid
  };
  return (
    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | குடும்ப வடிவம்</title>
        <link rel="canonical" href="/family/member-create" />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <form onSubmit={handleSubmit(handleFormSubmit)} className="auth_form" autoComplete="off">
              <h1>குடும்ப உறுப்பினர் படிவம்</h1>

              {/* Family Head Fields */}
              <div className="auth_element">
                <label className="auth_label">குடும்ப எண் </label>
                <input
                  type="text"
                  id="familyId"  // Changed the id to 'familyId' for clarity
                  value={familyId}  // This would be the value you want to display (passed as a prop or state)
                  readOnly  // Makes the input field read-only
                  className="auth_input"
                  placeholder="12345"  // Example placeholder, adjust as needed
                  {...register("familyId")}
                />
              </div>
              <div className="auth_element">
                <label className="auth_label">பெயர்(English)</label>
                <input
                  type="text"
                  id="tempFirstName"
                  value={nameData.tempFirstName || ""}
                  {...register("tempFirstName", {
                    required: "பெயர் தேவை",
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                    minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                  })}
                  className="auth_input"
                  placeholder="பொன்னாச்சி"
                  onChange={(e) => {
                    handleChange("tempFirstName");  // Trigger validation
                    handleNameChange("tempFirstName", e.target.value);  // Update state
                  }}
                />
                {errors.tempFirstName && <div className="auth_error">❗ {errors.tempFirstName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">தந்தை/கணவர் பெயர்(English)</label>
                <input
                  type="text"
                  id="tempLastName"
                  value={nameData.tempLastName || ""}
                  {...register("tempLastName", {
                    required: "பெயர் தேவை",
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                    minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                  })}
                  className="auth_input"
                  placeholder="அம்மன்"
                  onChange={(e) => {
                    handleChange("tempLastName");  // Trigger validation
                    handleNameChange("tempLastName", e.target.value);  // Update state
                  }}
                />
                {errors.tempLastName && <div className="auth_error">❗ {errors.tempLastName.message}</div>}
              </div>
              <button
                type="button"
                onClick={translateNameToTamil}
                disabled={loading}
              >
                தமிழில் மொழிபெயர்க்கவும்
              </button>
              <div className="auth_element">
                <label className="auth_label">பெயர்</label>
                <input
                  type="text"
                  id="firstName"
                  value={nameData.firstName || ""}
                  className="auth_input"
                  placeholder="பொன்னாச்சி"
                />
                {errors.firstName && <div className="auth_error">❗ {errors.firstName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">தந்தை/கணவர் பெயர்</label>
                <input
                  type="text"
                  id="lastName"
                  value={nameData.lastName || ""}
                  className="auth_input"
                  placeholder="அம்மன்"
                />
                {errors.lastName && <div className="auth_error">❗ {errors.lastName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">குடும்ப உறவு</label>
                <select
                  id="relationship"
                  {...register("relationship", { required: "உறவு தேர்ந்தெடுக்கவும்" })}
                  className="auth_select"
                >
                  <option value="">--உறவைத் தேர்ந்தெடுக்கவும்--</option>
                  {relationShips.map((relation) => (
                    <option key={relation.value} value={relation.value}>
                      {relation.label}
                    </option>
                  ))}
                </select>
                {errors.relationship && <div className="auth_error">❗ {errors.relationship.message}</div>}
              </div>
              {/* Parent ID Dropdown */}
              {!(relationship === "இல்லை" && validFamilyMembers.length === 0) && (
                <div className="auth_element">
                  <label className="auth_label">உறவினரர் (ஆதார் எண்)</label>
                  <select
                    id="parentId"
                    {...register("parentId", { required: "உறவினரர் தேர்ந்தெடுக்கவும்" })}
                    className="auth_select"
                  >
                    <option value="">--உறவினரைத் தேர்ந்தெடுக்கவும்--</option>
                    {familyData?.family?.familyMembers
                      ?.filter((member) => /^\d{12}$/.test(member.maskedAadhar)) // Only 12-digit Aadhaar
                      .map((member) => (
                        <option key={member.aadhar} value={member.aadhar}>
                          {member.firstName} {member.lastName} ({maskAadhar(member.maskedAadhar)})
                        </option>
                      ))}
                  </select>
                  {errors.parentId && <div className="auth_error">❗ {errors.parentId.message}</div>}
                </div>
              )}
              {relationship === "இல்லை" && validFamilyMembers.length === 0 && (
                <div className="auth_element">
                  <label className="auth_label">உறவினரர் (ஆதார் எண்) தேவையில்லை</label>
                </div>
              )}


              {/* Mobile Number Field (Now Optional) */}
              <div className="auth_element">
                <label className="auth_label">கைபேசி எண் (தேவையில்லை)</label>
                <input
                  type="tel"
                  id="mobile"
                  {...register("mobile", {
                    pattern: {
                      value: /^\d{10}$/,
                      message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                    },
                  })}
                  className="auth_input"
                  placeholder="1234567890"
                />
                {errors.mobile && <div className="auth_error">❗ {errors.mobile.message}</div>}
              </div>

              {/* Mobile Number Field */}
              <div className="auth_element">
                <label className="auth_label">கைபேசி எண் 2 (தேவையில்லை)</label>
                <input
                  type="tel"
                  id="mobile2"
                  {...register("mobile2", {

                    pattern: {
                      value: /^\+91[-]?\d{3}[-]?\d{3}[-]?\d{4}$|^(?:\+?\d{1,3})?[-.\s]?(\(?\d{2,3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                      message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                    },
                  })}
                  className="auth_input"
                  placeholder="+91-1234567890"
                />
                {errors.mobile2 && <div className="auth_error"> {errors.mobile2.message}</div>}
              </div>

              {/* Aadhar Number Field */}
              <div className="auth_element">
                <label className="auth_label">ஆதார் எண்</label>
                <input
                  type="text"
                  id="aadhar"
                  {...register("aadhar", {
                    required: "ஆதார் எண் தேவை",
                    pattern: {
                      value: /^(TMP\d{10}|TMP\d{12}_\d+|\d{12}|\d{4} \d{4} \d{4})$/,
                      message: "உதாரணம்: TMP1234567890, TMP123456789012_1, TMP123456789012_2, 00-இல் தொடங்கக்கூடாது",
                    },
                  })}
                  className="auth_input"
                  placeholder="1234 5678 9012"
                />
                {errors.aadhar && <div className="auth_error">❗ {errors.aadhar.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">மின்னஞ்சல்(தேவையில்லை)</label>
                <input
                  type="email"
                  id="email"
                  {...register("email", {

                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+(?:[a-zA-Z0-9-]*[a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, // Improved regex to match most valid email formats
                      message: "மின்னஞ்சல் செல்லுபடியாகாது",
                    },
                  })}
                  className="auth_input"
                  placeholder="ponnachiamman@gmail.com"
                />
                {errors.email && <div className="auth_error">❗ {errors.email.message}</div>}
              </div>

              {/* Optional Business Name Field */}
              <div className="auth_element">
                <label className="auth_label">வணிக பெயர் (தேவையில்லை)</label>
                <select
                  id="businessName"
                  {...register("businessName")}
                  className="auth_select"
                >
                  <option value="">--Select Business--</option>
                  {businessNames.map((business) => (
                    <option key={business.value} value={business.value}>
                      {business.label}
                    </option>
                  ))}
                </select>
                {errors.businessName && <div className="auth_error">❗ {errors.businessName.message}</div>}
              </div>
              {/* Date of Birth */}
              <div className="auth_element">
                <label className="auth_label">பிறந்த தேதி</label>

                <div style={{ display: "flex", gap: "10px" }}>
                  {/* Year dropdown */}
                  <select
                    onChange={handleYearChange}
                    value={selectedYear}
                    className="auth_input"
                  >
                    <option value="">ஆண்டு தேர்வு செய்யவும்</option>
                    {getYearOptions(1940).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  {/* Actual date input */}
                  <input
                    type="date"
                    id="dob"
                    ref={dateInputRef}
                    {...register("dob")}
                    className="auth_input"
                  />
                </div>

                {errors.dob && (
                  <div className="auth_error">❗ {errors.dob.message}</div>
                )}
              </div>

              <button type="submit" disabled={!isValid || isSubmitting}
                className={`auth_submit ${!isValid || isSubmitting ? "disabled_btn" : ""}`}
              >
                {loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பிக்கவும்'}
              </button>
            </form>
            <button onClick={() => navigate(`/family/view/${familyId}`)}>குடும்ப விவரங்கள்</button>

          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FamilyMemberCreate;
